import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { IFaqDto, IFaqGroupDto, IFaqGroupItemDto, IFaqFileSystemItemDto } from '../interfaces/Faq/ifaq-dto';

@Injectable({
  providedIn: 'root'
})
export class FaqService {
  private apiUrl = "";
  private uploadApiUrl = "";
  private menuChangedSubject = new Subject<void>();

  /** Emits when FAQ list/order/active state changes so FAQ/RESOURCES menu can refresh. */
  menuChanged$ = this.menuChangedSubject.asObservable();

  constructor(private http: HttpClient, @Inject('BASE_URL') baseUrl: string) {
    this.apiUrl = baseUrl + 'api/Faq/';
    this.uploadApiUrl = baseUrl + 'api/FaqUpload/';
  }

  notifyMenuChanged() {
    this.menuChangedSubject.next();
  }

  getFaqs(): Observable<IFaqDto[]> {
    return this.http.get<IFaqDto[]>(this.apiUrl + 'GetFaqs');
  }

  /**
   * Lightweight active FAQ list for FAQ/RESOURCES menu (no groups/items).
   * No browser cache — server IMemoryCache backs this API; SaveFaq clears it.
   */
  getActiveFaqsForMenu(): Observable<IFaqDto[]> {
    return this.http.get<IFaqDto[]>(this.apiUrl + 'GetActiveFaqsForMenu');
  }

  getFaqById(id: number): Observable<IFaqDto> {
    return this.http.get<IFaqDto>(this.apiUrl + 'GetFaqById/' + id);
  }

  getFaqByCode(code: string): Observable<IFaqDto> {
    return this.http.get<IFaqDto>(this.apiUrl + 'GetFaqByCode/' + code);
  }

  saveFaq(faq: IFaqDto): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl + 'SaveFaq', faq).pipe(
      tap(() => this.notifyMenuChanged())
    );
  }


  saveFaqGroup(group: IFaqGroupDto): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl + 'SaveFaqGroup', group);
  }


  saveFaqGroupItem(item: IFaqGroupItemDto): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl + 'SaveFaqGroupItem', item);
  }

  saveFaqGroups(groups: IFaqGroupDto[]): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl + 'SaveFaqGroups', groups);
  }

  saveFaqGroupItems(items: IFaqGroupItemDto[]): Observable<boolean> {
    return this.http.post<boolean>(this.apiUrl + 'SaveFaqGroupItems', items);
  }

  listFiles(subDir: string = ""): Observable<IFaqFileSystemItemDto[]> {
    return this.http.get<IFaqFileSystemItemDto[]>(this.uploadApiUrl + 'List?subDir=' + encodeURIComponent(subDir));
  }

  uploadFile(file: File, subDir: string = ""): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<any>(this.uploadApiUrl + 'Upload?subDir=' + encodeURIComponent(subDir), formData);
  }
}
